import React, { useRef, useLayoutEffect, useState } from 'react';
import { Grid } from '@mantine/core';
import { Outlet } from 'react-router-dom';
import EventsSection from './Events.section/Events.section';
import CalendarSection from './Calendar.section/Calendar.section';
import useDesktop from '@/hooks/useDesktop';
import { useCalendarEvents } from './useCalendarEvents';
import { HEADER_HEIGHT } from '@/constants';

export const CalendarPage: React.FC = () => {
  const isDesktop = useDesktop();
  const calendarRef = useRef<HTMLDivElement>(null);
  const [eventsHeight, setEventsHeight] = useState<string>('100%');

  const {
    calendarEvents,
    maxDate,
    minDate,
    isLoading,
    currentDate,
    handleDateChange,
    onSelectVendors,
    selectedEventType,
    onSelectEventType,
    vendors,
    selectedDate,
    handleSelectDate,
    selectedVendorIds,
    isRefetching,
  } = useCalendarEvents();

  useLayoutEffect(() => {
    if (!isDesktop || !calendarRef.current) {
      setEventsHeight('100%');
      return;
    }

    const updateHeight = () => {
      if (calendarRef.current) {
        const height = calendarRef.current.offsetHeight;
        const hasPageScrollbar = height + HEADER_HEIGHT > window.innerHeight;

        if (hasPageScrollbar) {
          setEventsHeight(`${height}px`);
        } else {
          setEventsHeight(`${window.innerHeight - HEADER_HEIGHT - 1}px`);
        }
      }
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(calendarRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isDesktop]);

  return (
    <>
      <Grid gutter={0} styles={{ inner: { height: '100%' } }}>
        <Grid.Col
          span={isDesktop ? 7 : 12}
          pt="lg"
          pr={isDesktop ? 'md' : 0}
          h="100%"
          ref={calendarRef}
        >
          <CalendarSection
            events={calendarEvents}
            maxDate={maxDate}
            minDate={minDate}
            currentDate={currentDate}
            handleDateChange={handleDateChange}
            isLoading={isLoading}
            selectedEventType={selectedEventType}
            onSelectEventType={onSelectEventType}
            onSelectVendors={onSelectVendors}
            vendors={vendors}
            handleSelectDate={handleSelectDate}
            selectedDate={selectedDate}
            isRefetching={isRefetching}
          />
        </Grid.Col>
        <Grid.Col span={isDesktop ? 5 : 12} pt="lg" pl="lg" bg="gray.1" h={eventsHeight}>
          <EventsSection
            selectedDate={selectedDate}
            maxDate={maxDate}
            calendarLoading={isLoading}
            selectedVendorIds={selectedVendorIds}
            selectedEventType={selectedEventType}
          />
        </Grid.Col>
      </Grid>
      <Outlet />
    </>
  );
};

export default CalendarPage;
